import { fontFaceRef, fontsMap, FontUrlResolver, type DocumentFont } from '@flobi/artwork-core';
import type { ParagraphText } from '@flobi/artwork-core/render';
import type { TypefaceFontProvider } from 'canvaskit-wasm';
import type * as Y from 'yjs';
import { askLibraryFont } from '../services/fonts';
import { cachedFetch } from '../services/mediaCache';
import { rememberFace } from './faceFiles';

/**
 * The fonts this document asked for, fetched and handed to the shaper.
 *
 * A family is chosen once and then belongs to the file: its face urls are written into
 * the document, so everyone who opens it — the next person, the export job, this editor
 * after a reload — loads the same files and sees the same line breaks. That is the whole
 * reason the urls live in the document rather than in a setting.
 *
 * A Flobi Fonts face is the exception to "the url in the document": Fonts hands out a
 * signed address that works for fifteen minutes, and only to someone who may still use the
 * font. So each face is asked for again by its `fontId` (or, in an older file, its
 * family's `assetId`) when the file opens, and the stored url is used only when that
 * fails and the url has not expired. Once loaded, a face's bytes stay with the canvas;
 * a retry asks again if the answer it had is near its end.
 *
 * Faces arrive after the first frame, so text drawn before one lands is drawn in the
 * fallback and redrawn when the real face is registered. The shaper's cache is emptied
 * at that moment, or it would go on handing out paragraphs shaped in the wrong font.
 */
/**
 * Why a family is not drawing, as the picker says it: `unavailable` when Fonts will not
 * give this person the font (refused, not found, no usable face), so trying again won't
 * help; `temporary` when it did not arrive this time (offline, a timeout, a server error).
 * The server's own wording stays in the console, never in the picker.
 */
export type FontProblem = 'unavailable' | 'temporary';

/** A face file the storage answered with an error status. */
class FaceFetchFailed extends Error {
  constructor(readonly status: number) {
    super(`the server answered ${status}`);
  }
}

/** The status an error carries: Fonts' own answer, the gateway's reply, or a face fetch. */
function statusOf(error: unknown): number | null {
  const e = error as { statusCode?: unknown; status?: unknown; response?: { status?: unknown } } | null;
  for (const value of [e?.statusCode, e?.status, e?.response?.status]) {
    if (typeof value === 'number' && value > 0) return value;
  }
  return null;
}

/** Sorts a failed face into one the person can't use and one worth trying again. */
export function fontProblemOf(error: unknown): FontProblem {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'temporary';
  const status = statusOf(error);
  if (error instanceof FaceFetchFailed) {
    // A fresh signed url refused (403) is fixed by asking Fonts again; a missing file is not.
    return status === 404 || status === 410 ? 'unavailable' : 'temporary';
  }
  if (status !== null) return status >= 500 || status === 408 || status === 429 ? 'temporary' : 'unavailable';
  // No status: the fetch itself failed (TypeError), or the request never got an answer.
  if (error instanceof TypeError) return 'temporary';
  const code = (error as { code?: unknown } | null)?.code;
  if ((error as { isAxiosError?: unknown } | null)?.isAxiosError === true || code === 'ERR_NETWORK' || code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
    return 'temporary';
  }
  // Fonts answered and said no ("Font not found."), or had no face Artwork can draw with.
  return 'unavailable';
}

/**
 * The families that failed to arrive, for the font picker to say so and to try again
 * (QA 2026-09-24, text-F24).
 *
 * A face that failed was noted with a console warning and never tried again, while the
 * picker showed the new font as chosen over text still drawn in the fallback. Module-wide
 * because one editor is open at a time and the picker sits in panels the canvas never
 * sees; the open document's loader registers itself here.
 */
const problems = new Map<string, FontProblem>();
const listeners = new Set<() => void>();
let version = 0;
let retrying: ((family: string) => void) | null = null;
/**
 * Fresh Flobi Fonts urls, shared by every file this tab opens: a font used in two files,
 * or by two faces of one family file, is asked for once until its url nears its end.
 */
export const documentFontUrls = new FontUrlResolver(askLibraryFont);

const told = () => {
  version++;
  for (const listener of listeners) listener();
};

export const fontProblems = {
  /** Why a family is not drawing, or null. */
  problemFor: (family: string): FontProblem | null => problems.get(family) ?? null,
  /** Fetches a family's failed faces again. */
  retry: (family: string): void => retrying?.(family),
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Changes whenever the list does, for useSyncExternalStore. */
  version: (): number => version,
};

export class DocumentFonts {
  private readonly loaded = new Set<string>();
  private readonly failed = new Map<string, string>();
  private readonly unsubscribe: () => void;

  constructor(
    private readonly root: Y.Doc,
    private readonly provider: TypefaceFontProvider,
    private readonly text: ParagraphText,
    private readonly onLoaded: () => void,
    private readonly urls: FontUrlResolver = documentFontUrls,
  ) {
    const map = fontsMap(root);
    this.unsubscribe = (() => {
      const listener = () => void this.catchUp();
      map.observe(listener);
      // Back online: whatever failed while the connection was down is fetched again.
      const online = () => this.retry();
      window.addEventListener('online', online);
      retrying = (family) => this.retry(family);
      return () => {
        map.unobserve(listener);
        window.removeEventListener('online', online);
      };
    })();
    void this.catchUp();
  }

  dispose(): void {
    this.unsubscribe();
    retrying = null;
    if (problems.size > 0) {
      problems.clear();
      told();
    }
  }

  /** Why a family is not drawing, for the editor to show. */
  errorFor(family: string): string | null {
    return this.failed.get(family) ?? null;
  }

  /** Forgets the failures of one family, or of all of them, and fetches those faces again. */
  retry(family?: string): void {
    const again = [...this.failed.keys()].filter((key) => family === undefined || key === family || key.startsWith(`${family}|`));
    if (again.length === 0) return;
    for (const key of again) {
      this.failed.delete(key);
      problems.delete(key);
    }
    told();
    void this.catchUp();
  }

  /** Fetches every face the document names and has not been loaded yet. */
  private async catchUp(): Promise<void> {
    const fonts = [...fontsMap(this.root).values()] as DocumentFont[];
    let added = false;
    for (const font of fonts) {
      for (const face of font.faces ?? []) {
        // A Fonts face is known by its id: its url changes every time someone picks it.
        const key = `${font.family}|${face.fontId ? `font:${face.fontId}` : face.url}`;
        if (this.loaded.has(key) || this.failed.has(key)) continue;
        this.loaded.add(key);
        let asked: 'resolved' | 'stored' = 'stored';
        try {
          const { url, from } = await this.urls.urlFor(font, face);
          asked = from;
          const response = await cachedFetch(url, { mode: 'cors' });
          if (!response.ok) throw new FaceFetchFailed(response.status);
          // Skia reads TrueType and OpenType; it has no woff2 decompressor, which is why
          // the backend asks Google for ttf rather than what a browser would take.
          if (face.format === 'woff2' || face.format === 'woff') {
            throw new Error(`${face.format} cannot be drawn with; ask for a ttf or otf face`);
          }
          const bytes = await response.arrayBuffer();
          this.provider.registerFont(bytes, font.family);
          rememberFace(font.family, bytes);
          added = true;
        } catch (error) {
          // A fresh url that was refused anyway is forgotten, so a retry asks Fonts again.
          const ref = asked === 'resolved' ? fontFaceRef(font, face) : null;
          if (ref) this.urls.forget(ref);
          this.loaded.delete(key);
          this.failed.set(key, (error as Error).message);
          this.failed.set(font.family, (error as Error).message);
          problems.set(font.family, fontProblemOf(error));
          told();
          console.warn(`Could not load the font ${font.family}: ${(error as Error).message}`);
        }
      }
    }
    if (!added) return;
    // Paragraphs shaped before the face arrived were shaped in the wrong font.
    this.text.invalidate();
    this.onLoaded();
  }
}
